import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, Panel, formatEur } from "@/components/shared/PageUi";
import { toast } from "sonner";
import {
  Save, Loader2, ArrowLeft, FilePlus2, Calculator, CheckCircle2, Receipt, Banknote,
  Briefcase, RefreshCw,
} from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const lastOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10); };

const defaultPayslip = {
  employee_id: "",
  pay_period_start: firstOfMonth(),
  pay_period_end: lastOfMonth(),
  pay_date: today(),
  worked_hours: 0,
  earnings: {
    base_salary: 0, overtime: 0, sunday_bonus: 0, evening_bonus: 0,
    night_bonus: 0, holiday_bonus: 0, vacation_pay: 0, other_taxable: 0,
  },
  benefits_override: null,
  tax_base_percent: null,
  tax_additional_percent: null,
  tax_income_threshold: null,
  tyel_percent_override: null,
  unemployment_percent: null,
  health_insurance_percent: null,
  union_fee_percent: null,
  other_deductions: 0,
  notes: "",
  status: "draft",
};

export default function PayslipForm() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const presetEmployee = params.get("employee");
  const navigate = useNavigate();

  const [employees, setEmployees] = useState([]);
  const [data, setData] = useState(defaultPayslip);
  const [calc, setCalc] = useState(null);
  const [calcing, setCalcing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load employees + existing payslip
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/payroll/employees?is_active=true");
        setEmployees(r.data || []);
        if (id) {
          const ps = await api.get(`/payroll/payslips/${id}`);
          const p = ps.data;
          setData({
            employee_id: p.employee_id,
            pay_period_start: p.pay_period_start,
            pay_period_end: p.pay_period_end,
            pay_date: p.pay_date,
            worked_hours: p.worked_hours || 0,
            earnings: p.earnings || defaultPayslip.earnings,
            benefits_override: p.benefits || null,
            tax_base_percent: p.deductions?.tax_base_percent_used ?? null,
            tax_additional_percent: p.deductions?.tax_additional_percent_used ?? null,
            tax_income_threshold: p.deductions?.tax_income_threshold ?? null,
            tyel_percent_override: p.deductions?.tyel_percent_used ?? null,
            unemployment_percent: p.deductions?.unemployment_percent_used ?? null,
            health_insurance_percent: p.deductions?.health_insurance_percent_used ?? null,
            union_fee_percent: p.deductions?.union_fee_percent_used ?? null,
            other_deductions: p.deductions?.other_deductions || 0,
            notes: p.notes || "",
            status: p.status,
          });
        } else if (presetEmployee) {
          // esitäytä työntekijällä + kk-palkalla
          const e = (r.data || []).find(x => x.id === presetEmployee);
          if (e) {
            setData(d => ({
              ...d,
              employee_id: e.id,
              earnings: { ...d.earnings, base_salary: e.monthly_salary || 0 },
              worked_hours: e.regular_hours_per_month || 0,
            }));
          }
        }
      } catch (e) { toast.error(formatApiError(e)); }
      setLoading(false);
    })();
  }, [id, presetEmployee]);

  // Auto-täydennä työntekijän tiedot vaihdettaessa
  const onEmployeeChange = (empId) => {
    const e = employees.find(x => x.id === empId);
    if (!e) return setData({ ...data, employee_id: empId });
    setData(prev => ({
      ...prev,
      employee_id: empId,
      earnings: {
        ...prev.earnings,
        base_salary: e.employment_type === "monthly" ? (e.monthly_salary || 0) : 0,
      },
      worked_hours: e.employment_type === "hourly" ? 0 : (e.regular_hours_per_month || 0),
    }));
  };

  // Reaaliaikainen laskenta
  const runCalc = useCallback(async () => {
    if (!data.employee_id) { setCalc(null); return; }
    setCalcing(true);
    try {
      const r = await api.post("/payroll/payslips/preview", data);
      setCalc(r.data);
    } catch (e) { /* hiljaisesti, näytetään virhe muualla */ }
    setCalcing(false);
  }, [data]);

  // Debounce calc
  useEffect(() => {
    const t = setTimeout(runCalc, 350);
    return () => clearTimeout(t);
  }, [runCalc]);

  // Auto-laske tuntipalkka * tunnit
  useEffect(() => {
    const e = employees.find(x => x.id === data.employee_id);
    if (!e || e.employment_type !== "hourly") return;
    if (data.worked_hours > 0 && e.hourly_rate > 0) {
      const base = +(data.worked_hours * e.hourly_rate).toFixed(2);
      if (Math.abs(base - data.earnings.base_salary) > 0.01) {
        setData(prev => ({ ...prev, earnings: { ...prev.earnings, base_salary: base } }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.worked_hours, data.employee_id]);

  const updE = (k, v) => setData({ ...data, earnings: { ...data.earnings, [k]: parseFloat(v) || 0 } });
  const upd = (k, v) => setData({ ...data, [k]: v });

  const save = async (finalStatus = "draft") => {
    if (!data.employee_id) return toast.error("Valitse työntekijä");
    setSaving(true);
    try {
      const payload = { ...data, status: finalStatus };
      if (id) {
        await api.put(`/payroll/payslips/${id}`, payload);
        toast.success(finalStatus === "paid" ? "Tallennettu ja merkitty maksetuksi" : "Luonnos tallennettu");
      } else {
        await api.post("/payroll/payslips", payload);
        toast.success(finalStatus === "paid" ? "Palkkalaskelma maksettu" : "Luonnos tallennettu");
      }
      navigate("/hr");
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  const selectedEmp = useMemo(() => employees.find(e => e.id === data.employee_id), [employees, data.employee_id]);

  // Hae työvuorot kaudelta + täytä erittely automaattisesti
  const [importing, setImporting] = useState(false);
  const importFromShifts = async () => {
    if (!data.employee_id) return toast.error("Valitse työntekijä ensin");
    setImporting(true);
    try {
      const r = await api.get(
        `/shifts/aggregate-for-payroll?employee_id=${data.employee_id}` +
        `&period_start=${data.pay_period_start}&period_end=${data.pay_period_end}`
      );
      const b = r.data?.breakdown || {};
      setData(prev => ({
        ...prev,
        worked_hours: r.data.total_hours,
        earnings: {
          ...prev.earnings,
          base_salary: +(b.regular?.amount || 0),
          overtime: +(b.overtime?.amount || 0),
          sunday_bonus: +(b.sunday?.amount || 0),
          evening_bonus: +(b.evening?.amount || 0),
          night_bonus: +(b.night?.amount || 0),
        },
      }));
      toast.success(`Tuotu ${r.data.shift_count} vuoroa (${r.data.total_hours} h)`);
    } catch (e) { toast.error(formatApiError(e)); }
    setImporting(false);
  };

  if (loading) return <div className="text-center py-12 text-white/40">Ladataan…</div>;

  return (
    <div className="space-y-4" data-testid="payslip-form">
      <PageHeader
        kicker="Palkat"
        title={id ? `Muokkaa palkkalaskelmaa` : "Uusi palkkalaskelma"}
        description="Syötä erittely – ennakonpidätys, TyEL, TVR ja sava lasketaan automaattisesti."
        actions={
          <div className="flex gap-2">
            <Link to="/hr"><Button variant="ghost" className="inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Takaisin</Button></Link>
            <Button variant="outline" onClick={() => save("draft")} disabled={saving} className="inline-flex items-center gap-2 border-amber-400/30" data-testid="payslip-save-draft">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Tallenna luonnos
            </Button>
            <Button onClick={() => save("paid")} disabled={saving || !calc} className="btn-luxe inline-flex items-center gap-2" data-testid="payslip-save-paid">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Maksa nyt
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left – inputs */}
        <div className="lg:col-span-2 space-y-4">
          {/* Työntekijä + ajanjakso */}
          <Panel title="Työntekijä ja ajanjakso">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Työntekijä *">
                <select
                  value={data.employee_id}
                  onChange={e => onEmployeeChange(e.target.value)}
                  className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
                  data-testid="ps-employee"
                  disabled={!!id}
                >
                  <option value="">— valitse —</option>
                  {employees.map(e => (
                    <option key={e.id} value={e.id}>{e.first_name} {e.last_name} ({e.employment_type})</option>
                  ))}
                </select>
              </Field>
              <Field label="Tehdyt tunnit (tunti/keikkapalkkaisille)">
                <Input type="number" step="0.5" value={data.worked_hours} onChange={e => upd("worked_hours", parseFloat(e.target.value) || 0)} data-testid="ps-hours" />
              </Field>
              <Field label="Palkkakausi alkaa"><Input type="date" value={data.pay_period_start} onChange={e => upd("pay_period_start", e.target.value)} data-testid="ps-period-start" /></Field>
              <Field label="Palkkakausi päättyy"><Input type="date" value={data.pay_period_end} onChange={e => upd("pay_period_end", e.target.value)} data-testid="ps-period-end" /></Field>
              <Field label="Maksupäivä"><Input type="date" value={data.pay_date} onChange={e => upd("pay_date", e.target.value)} data-testid="ps-pay-date" /></Field>
            </div>
          </Panel>

          {/* Ansiot */}
          <Panel
            title="Ansiot (€) – syötä joko suoraan euromäärä tai tunnit × €/h"
            action={
              <Button
                onClick={importFromShifts}
                disabled={importing || !data.employee_id}
                variant="outline"
                size="sm"
                className="border-emerald-400/30 inline-flex items-center gap-1 text-emerald-300 hover:bg-emerald-500/10"
                data-testid="import-from-shifts"
              >
                {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Briefcase className="w-3.5 h-3.5" />}
                Hae vuoroista (auto)
              </Button>
            }
          >
            <div className="space-y-2">
              <EarningRow
                label="Peruspalkka"
                value={data.earnings.base_salary}
                onChange={v => updE("base_salary", v)}
                testid="ps-base"
                showHours
                hoursValue={data.worked_hours}
                rateValue={selectedEmp?.hourly_rate || 0}
                onHoursChange={v => upd("worked_hours", parseFloat(v) || 0)}
              />
              <EarningRow
                label="Ylityökorvaus (50% tai 100% lisä)"
                value={data.earnings.overtime}
                onChange={v => updE("overtime", v)}
                testid="ps-overtime"
                showHours
                defaultRate={(selectedEmp?.hourly_rate || 0) * 0.5}
              />
              <EarningRow
                label="Sunnuntaityölisä (+100%)"
                value={data.earnings.sunday_bonus}
                onChange={v => updE("sunday_bonus", v)}
                testid="ps-sunday"
                showHours
                defaultRate={selectedEmp?.hourly_rate || 0}
              />
              <EarningRow
                label="Iltatyölisä (+15–20%)"
                value={data.earnings.evening_bonus}
                onChange={v => updE("evening_bonus", v)}
                testid="ps-evening"
                showHours
                defaultRate={(selectedEmp?.hourly_rate || 0) * 0.15}
              />
              <EarningRow
                label="Yötyölisä (+30–50%)"
                value={data.earnings.night_bonus}
                onChange={v => updE("night_bonus", v)}
                testid="ps-night"
                showHours
                defaultRate={(selectedEmp?.hourly_rate || 0) * 0.3}
              />
              <EarningRow
                label="Lomaraha"
                value={data.earnings.holiday_bonus}
                onChange={v => updE("holiday_bonus", v)}
                testid="ps-holiday-bonus"
              />
              <EarningRow
                label="Lomapalkka / -korvaus"
                value={data.earnings.vacation_pay}
                onChange={v => updE("vacation_pay", v)}
              />
              <EarningRow
                label="Muut veronalaiset"
                value={data.earnings.other_taxable}
                onChange={v => updE("other_taxable", v)}
              />
            </div>
            {selectedEmp && (
              <p className="text-[11px] text-white/50 mt-3 leading-relaxed">
                Luontoisedut (verotusarvo): puhelin {formatEur(selectedEmp.benefits?.phone || 0)}, lounas {formatEur(selectedEmp.benefits?.lunch || 0)}, auto {formatEur(selectedEmp.benefits?.car || 0)}, asunto {formatEur(selectedEmp.benefits?.accommodation || 0)}.
                Luontoisedut sisältyvät bruttoon ja ovat ennakonpidätyksen alaisia.
              </p>
            )}
          </Panel>

          {/* Vähennysten ohitukset (advanced) */}
          <Panel title="Vähennysten ohitukset (vapaaehtoinen)">
            <p className="text-[11px] text-white/50 mb-3">Oletuksena käytetään työntekijän verokorttia + lakisääteisiä prosentteja (TyEL iän mukaan 7.15% tai 8.65%, TVR 1.50%, SaVa 1.36%). Voit halutessasi yliajaa.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="Verokortti perus-%"><Input type="number" step="0.1" value={data.tax_base_percent ?? ""} placeholder={selectedEmp?.tax_card?.base_percent} onChange={e => upd("tax_base_percent", e.target.value === "" ? null : parseFloat(e.target.value))} /></Field>
              <Field label="Verokortti lisä-%"><Input type="number" step="0.1" value={data.tax_additional_percent ?? ""} placeholder={selectedEmp?.tax_card?.additional_percent} onChange={e => upd("tax_additional_percent", e.target.value === "" ? null : parseFloat(e.target.value))} /></Field>
              <Field label="Tuloraja (vuosi €)"><Input type="number" value={data.tax_income_threshold ?? ""} placeholder={selectedEmp?.tax_card?.income_threshold} onChange={e => upd("tax_income_threshold", e.target.value === "" ? null : parseFloat(e.target.value))} /></Field>
              <Field label="TyEL %"><Input type="number" step="0.01" value={data.tyel_percent_override ?? ""} placeholder="auto" onChange={e => upd("tyel_percent_override", e.target.value === "" ? null : parseFloat(e.target.value))} /></Field>
              <Field label="Työttömyysvakuutus %"><Input type="number" step="0.01" value={data.unemployment_percent ?? ""} placeholder="1.50" onChange={e => upd("unemployment_percent", e.target.value === "" ? null : parseFloat(e.target.value))} /></Field>
              <Field label="Sairausvakuutus %"><Input type="number" step="0.01" value={data.health_insurance_percent ?? ""} placeholder="1.36" onChange={e => upd("health_insurance_percent", e.target.value === "" ? null : parseFloat(e.target.value))} /></Field>
              <Field label="Ay-jäsenmaksu %"><Input type="number" step="0.01" value={data.union_fee_percent ?? ""} placeholder={selectedEmp?.union_fee_percent} onChange={e => upd("union_fee_percent", e.target.value === "" ? null : parseFloat(e.target.value))} /></Field>
              <Field label="Muut vähennykset (€)"><Input type="number" step="0.01" value={data.other_deductions} onChange={e => upd("other_deductions", parseFloat(e.target.value) || 0)} /></Field>
            </div>
          </Panel>

          <Panel title="Muistiinpanot">
            <textarea value={data.notes} onChange={e => upd("notes", e.target.value)} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm min-h-[80px]" />
          </Panel>
        </div>

        {/* Right – live calc */}
        <div className="space-y-4">
          <Panel title={<span className="inline-flex items-center gap-2"><Calculator className="w-4 h-4" /> Laskenta {calcing && <Loader2 className="w-3 h-3 animate-spin text-white/40" />}</span>}>
            {!calc ? (
              <p className="text-xs text-white/40 italic">Valitse työntekijä ja syötä peruspalkka — laskenta ilmestyy tänne.</p>
            ) : (
              <div className="space-y-3 text-sm" data-testid="payslip-calc">
                {/* Ansiot */}
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-amber-200/70 mb-1">Ansiot</p>
                  {Object.entries(calc.earnings || {}).filter(([_, v]) => v > 0).map(([k, v]) => (
                    <Row key={k} label={LABELS[k] || k} value={v} />
                  ))}
                  <Row label="Rahapalkka yht." value={calc.earnings_total} bold />
                </div>

                {/* Luontoisedut */}
                {calc.benefits_total > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-amber-200/70 mb-1">Luontoisedut (vero-arvo)</p>
                    {Object.entries(calc.benefits || {}).filter(([_, v]) => v > 0).map(([k, v]) => (
                      <Row key={k} label={LABELS[k] || k} value={v} />
                    ))}
                    <Row label="Luontoisedut yht." value={calc.benefits_total} bold />
                  </div>
                )}

                <div className="border-t border-white/10 pt-2">
                  <Row label="BRUTTOPALKKA (veronalainen)" value={calc.gross_total} bold accent="text-blue-300" />
                </div>

                {/* Vähennykset */}
                <div className="border-t border-white/10 pt-2">
                  <p className="text-[10px] uppercase tracking-widest text-red-300/70 mb-1">Vähennykset</p>
                  <Row label={`Ennakonpidätys (${(calc.deductions.tax_base_percent_used || 0).toFixed(1)}% + ${(calc.deductions.tax_additional_percent_used || 0).toFixed(1)}%)`} value={calc.deductions.tax_withholding} neg />
                  <Row label={`TyEL ${calc.deductions.tyel_percent_used || 0}%`} value={calc.deductions.tyel} neg />
                  <Row label={`Työttömyysvakuutus ${calc.deductions.unemployment_percent_used || 0}%`} value={calc.deductions.unemployment} neg />
                  <Row label={`Sairausvakuutus ${calc.deductions.health_insurance_percent_used || 0}%`} value={calc.deductions.health_insurance} neg />
                  {calc.deductions.union_fee > 0 && <Row label="Ay-jäsenmaksu" value={calc.deductions.union_fee} neg />}
                  {calc.deductions.other_deductions > 0 && <Row label="Muut vähennykset" value={calc.deductions.other_deductions} neg />}
                  <Row label="Vähennykset yht." value={calc.deductions_total} bold neg />
                </div>

                {/* Netto */}
                <div className="border-t border-white/10 pt-2 bg-emerald-500/10 -mx-4 px-4 py-2 rounded-lg">
                  <Row label="NETTOPALKKA" value={calc.net_total} bold accent="text-emerald-300" big />
                </div>

                {/* YTD */}
                {calc.ytd && (
                  <div className="border-t border-white/10 pt-2">
                    <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Vuosikertymät (sis. tämä)</p>
                    <Row label="Brutto YTD" value={calc.ytd.gross} small />
                    <Row label="Ennakonpidätys YTD" value={calc.ytd.tax} small />
                    <Row label="TyEL YTD" value={calc.ytd.tyel} small />
                  </div>
                )}
              </div>
            )}
          </Panel>

          {selectedEmp && (
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 text-xs space-y-1">
              <p className="text-amber-200/70 uppercase text-[10px] tracking-widest">Työntekijän tiedot</p>
              <p className="text-white/80">{selectedEmp.first_name} {selectedEmp.last_name}</p>
              <p className="text-white/50 font-mono">{selectedEmp.national_id || "—"}</p>
              <p className="text-white/50">{selectedEmp.iban || "—"}</p>
              <p className="text-white/40 mt-2">
                Verokortti: {selectedEmp.tax_card?.base_percent || 0}% / {selectedEmp.tax_card?.additional_percent || 0}% · raja {formatEur(selectedEmp.tax_card?.income_threshold || 0)}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const LABELS = {
  base_salary: "Peruspalkka",
  overtime: "Ylityökorvaus",
  sunday_bonus: "Sunnuntaityölisä",
  evening_bonus: "Iltatyölisä",
  night_bonus: "Yötyölisä",
  holiday_bonus: "Lomaraha",
  vacation_pay: "Lomapalkka",
  other_taxable: "Muut veronalaiset",
  phone: "Puhelinetu",
  lunch: "Lounasetu",
  car: "Autoetu",
  accommodation: "Asunto-etu",
  other: "Muu etu",
};

function Row({ label, value, bold = false, neg = false, accent = "", big = false, small = false }) {
  return (
    <div className={`flex justify-between items-center ${small ? "text-[11px] text-white/50" : ""} ${big ? "text-lg py-1" : "py-0.5"} ${bold ? "font-semibold" : ""}`}>
      <span className={accent || "text-white/70"}>{label}</span>
      <span className={`font-mono ${neg ? "text-red-300" : accent || "text-white"}`}>
        {neg ? "-" : ""}{formatEur(value)}
      </span>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-white/60">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/**
 * EarningRow – yksittäinen tulorivi, jossa voi syöttää joko suoraan euron tai erittelyn (tunnit × €/h = lasku).
 * Auto-laskenta: hours × rate = amount. Käyttäjä voi yliajaa amountin manuaalisesti.
 */
function EarningRow({ label, value, onChange, testid, showHours = false, hoursValue, rateValue, defaultRate = 0, onHoursChange }) {
  const [breakdown, setBreakdown] = React.useState(false);
  const [hours, setHours] = React.useState(hoursValue ?? 0);
  const [rate, setRate] = React.useState(rateValue ?? defaultRate ?? 0);

  React.useEffect(() => {
    if (hoursValue !== undefined) setHours(hoursValue);
  }, [hoursValue]);
  React.useEffect(() => {
    if (rateValue !== undefined && rateValue !== 0) setRate(rateValue);
    else if (defaultRate && rate === 0) setRate(defaultRate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateValue, defaultRate]);

  const recalc = (h, r) => {
    const amt = +(h * r).toFixed(2);
    onChange(amt);
    if (onHoursChange) onHoursChange(h);
  };

  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3" data-testid={`earn-row-${testid || label}`}>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-sm text-white/80 font-medium">{label}</Label>
        {showHours && (
          <button
            type="button"
            onClick={() => setBreakdown(!breakdown)}
            className="text-[10px] uppercase tracking-widest text-amber-300 hover:text-amber-200 px-2 py-1 rounded transition"
            data-testid={`breakdown-toggle-${testid || label}`}
          >
            {breakdown ? "Suora euro" : "Erittele (tunnit × €/h)"}
          </button>
        )}
      </div>

      {showHours && breakdown ? (
        <div className="grid grid-cols-12 gap-2 items-end">
          <div className="col-span-4">
            <Label className="text-[10px] uppercase tracking-widest text-white/40">Tunnit</Label>
            <Input
              type="number" step="0.25" inputMode="decimal"
              value={hours}
              onChange={e => { const h = parseFloat(e.target.value) || 0; setHours(h); recalc(h, rate); }}
              className="text-base h-11 text-center font-mono"
              data-testid={`hours-${testid || label}`}
            />
          </div>
          <div className="col-span-1 text-center text-white/40 pb-3">×</div>
          <div className="col-span-3">
            <Label className="text-[10px] uppercase tracking-widest text-white/40">€/h</Label>
            <Input
              type="number" step="0.01" inputMode="decimal"
              value={rate}
              onChange={e => { const r = parseFloat(e.target.value) || 0; setRate(r); recalc(hours, r); }}
              className="text-base h-11 text-center font-mono"
              data-testid={`rate-${testid || label}`}
            />
          </div>
          <div className="col-span-1 text-center text-white/40 pb-3">=</div>
          <div className="col-span-3">
            <Label className="text-[10px] uppercase tracking-widest text-amber-300">Yhteensä €</Label>
            <Input
              type="number" step="0.01" inputMode="decimal"
              value={value}
              onChange={e => onChange(parseFloat(e.target.value) || 0)}
              className="text-base h-11 text-right font-mono text-emerald-300 bg-emerald-500/5 border-emerald-500/30"
              data-testid={`amount-${testid || label}`}
            />
          </div>
        </div>
      ) : (
        <Input
          type="number" step="0.01" inputMode="decimal"
          value={value}
          onChange={e => onChange(parseFloat(e.target.value) || 0)}
          placeholder="0.00 €"
          className="text-base h-11 text-right font-mono"
          data-testid={testid}
        />
      )}
    </div>
  );
}
